import * as React from "react";
import { useLoaderData, useNavigate} from 'react-router-dom';
import Container from "@mui/material/Container";
import Grid from "@mui/material/Unstable_Grid2";
import AddIcon from "@mui/icons-material/Add";
import Header from "../components/Header";

import {useForm, FormProvider} from 'react-hook-form';
import Form from "../components/form/Form";
import FormButton from "../components/form/FormButton";
import FormDatePicker from "../components/form/FormDatePicker";
import FormMultiSelectChipsFilter from "../components/form/FormMultiSelectChipsFilter";
import FormDropDown from "../components/form/FormDropDown";
import FormNumberTextField from "../components/form/FormNumberTextField";
import FormTextField from "../components/form/FormTextField";
import FormSwitch from "../components/form/FormSwitch";
import { listAnimals } from "../models/animals";
import { listAssessments } from "../models/assessments";
import { listAssessmentTypes } from "../models/assessmentTypes";
import { startNewAssessment } from "../models/assessmentOperations";
import { getFacility } from "../models/facility";
import { useAuth } from "../context/AuthProvider";
import {useMessage} from '../context/MessageProvider';
import dayjs from "dayjs";

import { useConnectivity } from "../context/ConnectivityProvider";

import { isInProgress } from '../utils/assessments';
import { date } from "../utils/datetime";
import { name as speciesName } from "../utils/species";
import { name as animalName } from "../utils/animal";


export async function loader() {
  const animals = await listAnimals();
  const assessments = await listAssessments();
  const facility = await getFacility();
  const assessmentTypes = await listAssessmentTypes();
  return { animals, assessments, facility, assessmentTypes };
}

export default function AssessmentsFormPage({ title = "Assessments", ...props }) {
  const {setSuccess, setError} = useMessage();
  const { animals, assessments, facility, assessmentTypes } = useLoaderData();

  //const [selected, setSelected] = useState([]);
  const {facilityId, user, profile} = useAuth();
  const navigate = useNavigate();
  const {isOnline} = useConnectivity();

  const [working,setWorking] = React.useState(false);


  const facilityTimeframes = (facility?.workday_timeframes?.length)
    ? facility.workday_timeframes
    : (facility?.start_of_day && facility?.end_of_day)
      ? [{start_of_day: facility.start_of_day, end_of_day: facility.end_of_day}]
      : [];

  const formatTimeframeLabel = (timeframe, index) => {
    const startLabel = timeframe?.start_of_day?.format ? timeframe.start_of_day.format("HH:mm") : "";
    const endLabel = timeframe?.end_of_day?.format ? timeframe.end_of_day.format("HH:mm") : "";
    if (startLabel && endLabel) return `${startLabel} - ${endLabel}`;
    return `Timeframe ${index + 1}`;
  };

  const timeframeOptions = facilityTimeframes.map((cur, index)=>({
    value: String(index),
    label: formatTimeframeLabel(cur, index),
  }));
  const defaultTimeframeIndex = timeframeOptions[0]?.value ?? "";

  const assessmentTypeOptions = [
    { value: "", label: "Default" },
    ...assessmentTypes.map((cur)=>({
      value: cur.id,
      label: cur.name,
    })),
  ];

  const onSubmit = async (data) => {
    try{
      setWorking(true);
      console.log("handleSubmit data: ", data);

      const parsedTimeframeIndex = Number.isInteger(data.timeframe_index)
        ? data.timeframe_index
        : Number.parseInt(data.timeframe_index, 10);
      const timeframeIndex = Number.isFinite(parsedTimeframeIndex) ? parsedTimeframeIndex : 0;
      const selectedTimeframe = facilityTimeframes[timeframeIndex];
      if (!selectedTimeframe) {
        throw new Error("No workday timeframe selected");
      }

      const selectedAssessmentType = assessmentTypes.find((cur)=>cur.id === data.assessment_type_id) || null;

      const savePromises = (data.animal_ids || []).map(animalId => {
        const isVisibleInReports = data.is_visibile_in_reports !== false;
        const matchedAnimal = animals.find((cur)=>cur.id === animalId);
        const animalName = matchedAnimal?.name || "Animal";
        const animalSpecies = matchedAnimal?.species || "";
        const userName = profile?.name || user?.name || user?.user_metadata?.name || "";
        const template = data.assessment_title || "{{animal}}";
        const formattedDate = date(data.date);
        const resolvedTitle = template
          .replaceAll("{{animal}}", animalName)
          .replaceAll("{{species}}", animalSpecies)
          .replaceAll("{{user}}", userName)
          .replaceAll("{{date}}", formattedDate || "");

        return startNewAssessment({
          started_on: data.date,
          animal_id: animalId,
          facility_id: facilityId,
          user_id: user.id,
          is_online: isOnline,
          override_section_a_observations_total: data.override_section_a_observations_total,
          override_section_b_observations_total: data.override_section_b_observations_total,
          start_of_day: selectedTimeframe.start_of_day,
          end_of_day: selectedTimeframe.end_of_day,
          assessment_type_id: selectedAssessmentType?.id || null,
          assessment_title: resolvedTitle,
          is_visibile_in_reports: isVisibleInReports,
        });
      });
      const promiseSaveResults = await Promise.all(savePromises);
      console.log("assessment saveResults: ", promiseSaveResults);
      setSuccess("Assessments Started");
      navigate("/assessments");
    }
    catch(e){
      console.error(e)
      setError(e);
    } finally {
      setWorking(false);
    }
  };


  const defaultValues = {
    date: dayjs(),
    animal_ids: [],
    timeframe_index: timeframeOptions[0]?.value ?? "",
    assessment_type_id: "",
    assessment_title: "{{animal}}",
    is_visibile_in_reports: true,
    override_section_a_observations_total: facility.section_a_observations_total,
    override_section_b_observations_total: facility.section_b_observations_total
  };
  const formProps = { defaultValues };

	const methods = useForm(formProps);
  const { setValue, watch } = methods;

  const selectedAssessmentTypeId = watch("assessment_type_id");
  const selectedAssessmentType = assessmentTypes.find((cur)=>cur.id === selectedAssessmentTypeId) || null;

  React.useEffect(()=>{
    const defaultAssessmentTitle = selectedAssessmentType?.default_assessment_title || "{{animal}}";
    const parsedPreferredTimeframeIndex = Number.parseInt(selectedAssessmentType?.preferred_timeframe_index ?? "", 10);
    const preferredTimeframeIsValid = Number.isFinite(parsedPreferredTimeframeIndex)
      && parsedPreferredTimeframeIndex >= 0
      && parsedPreferredTimeframeIndex < facilityTimeframes.length;
    const preferredTimeframeIndex = preferredTimeframeIsValid
      ? String(parsedPreferredTimeframeIndex)
      : defaultTimeframeIndex;

    if (selectedAssessmentTypeId === "") {
      setValue("assessment_title", "{{animal}}");
      setValue("timeframe_index", defaultTimeframeIndex);
      setValue("override_section_a_observations_total", facility.section_a_observations_total);
      setValue("override_section_b_observations_total", facility.section_b_observations_total);
      return;
    }

    setValue("assessment_title", defaultAssessmentTitle);
    setValue("timeframe_index", preferredTimeframeIndex);

    if (selectedAssessmentType?.section_a_observations_total) {
      setValue("override_section_a_observations_total", selectedAssessmentType.section_a_observations_total);
    }

    if (selectedAssessmentType?.section_b_observations_total) {
      setValue("override_section_b_observations_total", selectedAssessmentType.section_b_observations_total);
    }
  }, [selectedAssessmentTypeId, selectedAssessmentType, setValue, defaultTimeframeIndex, facility.section_a_observations_total, facility.section_b_observations_total, facilityTimeframes.length]);

  const inProgressAssessments = assessments.filter((cur)=>{
    return isInProgress(cur);
  });
  const animalIdsThatAreInProgress = inProgressAssessments.map((cur)=>cur.animal_id);

  const animalsNotBeingAssessed = animals.filter((animal)=>{
    return animalIdsThatAreInProgress.includes(animal.id) === false;
  })

  const allowMultipleOpen = selectedAssessmentType
    ? !selectedAssessmentType.allow_single_open_assessment_per_animal
    : false;
  const baseAnimals = allowMultipleOpen ? animals : animalsNotBeingAssessed;

  const speciesFilter = (selectedAssessmentType?.species || []).map((cur)=>String(cur).toLowerCase().trim()).filter(Boolean);
  const filteredAnimals = speciesFilter.length
    ? baseAnimals.filter((animal)=>{
        const speciesValue = String(animal.species || '').toLowerCase().trim();
        return speciesFilter.includes(speciesValue);
      })
    : baseAnimals;

  const animalList = filteredAnimals.map((cur)=>{
    return {
      value: cur.id,
      label: animalName(cur),
    };
  }).sort((a, b) => a.label.localeCompare(b.label));

  const speciesFilterList = filteredAnimals.reduce((acc, cur) => {
    if (!acc.includes(cur.species)) acc.push(cur.species);
    return acc;
  }, []).map((cur) => ({ value: cur, label: speciesName(cur) }));


  return (
    <Container component="main" maxWidth="md">
      <Header label={title} />
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <Grid rowSpacing={4} container spacing={2} alignItems="center">
            <Grid item xs={12} s={3}>
              <FormDatePicker name="date" label="Start Date" required />
            </Grid>

            <Grid xs={12}>
              <FormDropDown name="assessment_type_id" label="Assessment Type" list={assessmentTypeOptions} />
            </Grid>

            <Grid xs={12}>
              <FormTextField
                name="assessment_title"
                label="Assessment Title"
                helperText="Supports {{animal}}, {{species}}, {{user}}, and {{date}}"
              />
            </Grid>

            <Grid xs={12}>
              <FormSwitch
                name="is_visibile_in_reports"
                label="Visible in reports"
              />
            </Grid>

            <Grid xs={12}>
              <FormMultiSelectChipsFilter name="animal_ids" label="Animals To Assess" required list={animalList} enableSelectAll={true} enableTextFilter={true} enableSelectFilter={true} selectFilterList={speciesFilterList} />
            </Grid>

            <Grid xs={12} sm={6}>
              <FormDropDown name="timeframe_index" label="Workday Timeframe" list={timeframeOptions} required />
            </Grid>

            <Grid xs={12} sm={6}>
              <FormNumberTextField name="override_section_a_observations_total" label="Section A Observations Total" required min={4} max={12} />
            </Grid>

            <Grid xs={12} sm={6}>
              <FormNumberTextField name="override_section_b_observations_total" label="Section B Observations Total" required min={4} max={36}/>
            </Grid>


            <Grid container xs={12} marginBottom={2} justifyContent="flex-end">
              <Grid>
                <FormButton working={working}>
                  <AddIcon />
                  Add Assessment(s)
                </FormButton>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      </FormProvider>
    </Container>
  );
}
